import React from "react";
import tw from "twin.macro";
import { __IS_DEV__, hasItems } from "~/features/app/utils";
import { type Theme, type Mode, getThemeStyle } from "~/features/ui/get-theme-style";
import { Wrap } from "~/features/ui/wrap";
import { RichText } from "~/features/ui/rich-text";
import { Headline } from "~/features/ui/headline";
import { Slider } from "~/features/ui/slider";
import { Progress } from "~/features/ui/slider/progress";
import { Grid } from "~/features/ui/grid";

type HeaderProps = {
  headline?: string;
  text?: string;
  link?: React.ReactNode;
  alignment: "horizontal" | "vertical";
};

export type Props = {
  headline?: string;
  text?: string;
  link?: React.ReactNode;
  children: React.ReactNode[];
  theme: Theme;
  mode: Mode;
  mobileLayout?: "slider" | "grid";
  hasTopDivider?: boolean;
  hideDividers?: boolean;
  whiteSurface?: boolean;
};

/*
  This component handles a common pattern in the project, where we have a section with 1-3 items and a headline
  The items are displayed in a 3 column grid on desktop and in a slider on mobile
  The desktop grid changes layout depending on the number of items:
    * 1 item: headline in first column, item span across the second and third column
    * 2 items: headline in first column, item 1 in second and item 2 in third column
    * 3 items: headline is above the grid and each item is display in 1 column
*/

function HorizontalHeader(props: HeaderProps) {
  const { headline, text, link } = props;

  return (
    <Grid tw="gap-y-16">
      {headline && (
        <div css={[tw`col-span-full lg:col-span-7`]}>
          <Headline type="h2" text={headline} tw="text-mobile-title-20-normal lg:text-desktop-title-20-normal" />
        </div>
      )}
      {(link || text) && (
        <div css={[tw`col-span-full flex flex-col gap-y-16 lg:(place-self-end col-span-5 gap-y-24)`]}>
          {text && <RichText text={text} />}
          {link && <div>{link}</div>}
        </div>
      )}
    </Grid>
  );
}

function VerticalHeader(props: HeaderProps) {
  const { headline, text, link } = props;

  return (
    <div tw="flex flex-col gap-y-16 h-full">
      {headline && (
        <Headline type="h2" text={headline} tw="text-mobile-title-20-normal lg:text-desktop-title-20-normal" />
      )}
      {(link || text) && (
        <div css={[tw`flex flex-col gap-y-16 mt-auto`]}>
          {text && <RichText text={text} />}
          {link && <div>{link}</div>}
        </div>
      )}
    </div>
  );
}

function Header(props: HeaderProps) {
  return props.alignment === "horizontal" ? <HorizontalHeader {...props} /> : <VerticalHeader {...props} />;
}

export function AdaptiveSection(props: Props & React.ComponentPropsWithRef<"section">) {
  const {
    headline,
    text,
    children,
    theme,
    mode,
    link,
    mobileLayout = "slider",
    hasTopDivider,
    hideDividers = false,
    whiteSurface = false,
    ...rest
  } = props;
  const themeStyle = getThemeStyle({ mode: mode as Mode, theme: theme as Theme });
  // Opt-in true-white surface (the theme helper maps "white" to grey-20).
  const surface = whiteSurface ? tw`bg-primary` : themeStyle.background;
  const [activeSlideIndex, setActiveSlideIndex] = React.useState(0);

  const hasHeaderData = Boolean(headline || text || link);
  const useSingleRow = children.length === 1 && !hasHeaderData;

  if (!hasItems(children)) {
    return null;
  }
  if (children.length > 3 && __IS_DEV__) {
    console.warn("AdaptiveSection: This component only supports 1-3 items");
  }

  const items = children.slice(0, 3);
  const renderSlider = items.length > 1 && mobileLayout === "slider";

  return (
    <section
      data-theme={mode as Mode}
      css={[hasTopDivider && tw`border-t`, themeStyle.text, themeStyle.border, surface]}
      {...rest}
    >
      <Wrap mode={mode as Mode}>
        {hasHeaderData && items.length === 3 && (
          <div
            css={[
              tw`relative z-10 flex flex-col gap-16 p-40 border-b lg:(flex-row p-80 gap-20)`,
              themeStyle.border,
              surface,
            ]}
          >
            <Header
              alignment={items.length === 3 ? "horizontal" : "vertical"}
              headline={headline}
              text={text}
              link={link}
            />
          </div>
        )}
        <div css={[tw`relative z-10 lg:hidden`, themeStyle.border, surface]}>
          {renderSlider ? (
            <>
              {items.length < 3 && !useSingleRow && hasHeaderData && (
                <div css={[tw`p-40 w-full border-b`, themeStyle.border]}>
                  <Header alignment="vertical" headline={headline} text={text} link={link} />
                </div>
              )}
              <Slider
                onMove={(splide) => setActiveSlideIndex(splide.Components.Controller.getIndex())}
                options={{
                  fixedWidth: "90%",
                }}
              >
                {items.map((child, idx) => (
                  <Slider.Slide
                    key={idx}
                    css={[tw`p-16`, !hideDividers && tw`not-first:border-l`, surface, themeStyle.border]}
                  >
                    {child}
                  </Slider.Slide>
                ))}
              </Slider>
              <div css={[tw`px-16 py-32 border-t lg:(px-40 py-24)`, surface, themeStyle.border]}>
                <Progress activeSlideIndex={activeSlideIndex} totalSlides={items.length} />
              </div>
            </>
          ) : (
            <div css={[tw`divide-y`, themeStyle.border]}>
              {items.map((child, idx) => (
                <div tw="p-16" key={idx}>
                  {child}
                </div>
              ))}
            </div>
          )}
        </div>
        <div css={[tw`relative z-10 hidden px-40 lg:block`, themeStyle.border, surface]}>
          <div css={[tw`flex overflow-hidden`, !hideDividers && tw`border-x divide-x`, themeStyle.border]}>
            {items.length < 3 && !useSingleRow && hasHeaderData && (
              <div css={[tw`p-40 flex flex-col justify-between basis-1/3 w-full`]}>
                <Header alignment="vertical" headline={headline} text={text} link={link} />
              </div>
            )}
            {items.map((child, idx) => (
              <div
                key={idx}
                css={[
                  tw`w-full p-40 basis-1/3`,
                  hasHeaderData && children.length === 1 && tw`basis-2/3`,
                  !hasHeaderData && children.length === 2 && tw`basis-1/2`,
                  !hasHeaderData && children.length === 1 && tw`basis-full`,
                ]}
              >
                {child}
              </div>
            ))}
          </div>
        </div>
      </Wrap>
    </section>
  );
}
